"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelectComplexOptions = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
// Based loosely on code from https://www.w3.org/TR/wai-aria-practices/examples/combobox/combobox-select-only.html
const grins_utils_1 = require("grins-utils");
const react_1 = require("react");
const classnames_1 = __importDefault(require("classnames"));
const nanoid_1 = require("nanoid");
const constants_1 = require("../constants");
const text_input_1 = require("../styles/text-input");
function SelectComplexOptions({ className, helpText, inputName, label, onSelectOption, options, selectedOption, autoCompleteName, disabled, testId, }) {
    const [open, setOpen] = (0, react_1.useState)(false);
    const [focused, setFocused] = (0, react_1.useState)(false);
    const [activeIndex, setActiveIndex] = (0, react_1.useState)(0);
    const [id] = (0, react_1.useState)(() => `select:${(0, nanoid_1.nanoid)()}`); // useState to avoid calling nanoid() every render
    const searchString = (0, react_1.useRef)("");
    const searchTimeout = (0, react_1.useRef)(null);
    const comboboxRef = (0, react_1.useRef)(null);
    const listboxRef = (0, react_1.useRef)(null);
    const idLabel = `${id}:label`;
    const idListbox = `${id}:listbox`;
    const idOption = (index) => `${id}:option-${index}`;
    const isOffset = focused || selectedOption !== null;
    const scrollOptionIntoView = (optionIndex, delay) => {
        // We use a delay sometimes because the listbox must become visible before
        // scrolling into view will work
        if (delay === undefined)
            delay = !open;
        if (!listboxRef.current)
            return;
        if (delay) {
            window.setTimeout(() => scrollOptionIntoView(optionIndex, false), 0);
        }
        else if (listboxRef.current) {
            listboxRef.current.children[optionIndex]?.scrollIntoView({
                block: "nearest",
            });
        }
    };
    const consumeEvent = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    const openListbox = (scrollToActive) => {
        setOpen(true);
        if (comboboxRef.current)
            comboboxRef.current.focus();
        if (scrollToActive)
            scrollOptionIntoView(activeIndex);
    };
    const closeListbox = () => {
        setOpen(false);
    };
    const selectOption = (selectedOptionIndex) => {
        activateOption(selectedOptionIndex);
        closeListbox();
        onSelectOption(selectedOptionIndex);
    };
    const selectActiveOption = () => {
        closeListbox();
        onSelectOption(activeIndex);
    };
    const activateOption = (activateOptionIndex) => {
        setActiveIndex(activateOptionIndex);
        scrollOptionIntoView(activateOptionIndex);
    };
    const findPrefixMatch = (orderedOptions, search) => {
        return orderedOptions.find(option => {
            const shortOption = option.uiLabel.slice(0, search.length);
            return (shortOption.localeCompare(search, undefined, {
                usage: "search",
                sensitivity: "base",
            }) === 0);
        });
    };
    const activateSearchedOption = (search) => {
        const orderedOptions = [
            // start searching from the element after the active index
            ...options.slice(activeIndex + 1),
            ...options.slice(0, activeIndex + 1),
        ];
        const exactMatch = findPrefixMatch(orderedOptions, search);
        if (exactMatch !== undefined)
            return activateOption(options.indexOf(exactMatch));
        const allSameLetter = search.split("").every(letter => letter === search[0]);
        if (allSameLetter) {
            const sameLetterMatch = findPrefixMatch(orderedOptions, search[0]);
            if (sameLetterMatch !== undefined)
                return activateOption(options.indexOf(sameLetterMatch));
        }
    };
    const handleLabelMouseDown = event => {
        if (open)
            closeListbox();
        else
            openListbox(true);
        event.preventDefault(); // keep focus from leaving the combobox
    };
    const handleFocus = () => {
        setFocused(true);
    };
    const handleBlur = () => {
        setFocused(false);
        // Tab was pressed, or an outside click happened while menu was open
        if (open)
            selectActiveOption();
    };
    const handleSearchKey = (key) => {
        if (searchTimeout.current) {
            window.clearTimeout(searchTimeout.current);
            searchTimeout.current = null;
        }
        searchString.current += key;
        searchTimeout.current = window.setTimeout(() => (searchString.current = ""), 500);
        activateSearchedOption(searchString.current);
    };
    const handleKeyGeneral = (event) => {
        const { key, ctrlKey, altKey, metaKey } = event;
        if (key === "Home") {
            openListbox(false);
            activateOption(0);
            consumeEvent(event);
        }
        else if (key === "End") {
            openListbox(false);
            activateOption(options.length - 1);
            consumeEvent(event);
        }
        else if (!ctrlKey &&
            !altKey &&
            !metaKey &&
            key.length === 1 &&
            key !== " ") {
            openListbox(false);
            handleSearchKey(key);
            consumeEvent(event);
        } // else, do nothing
    };
    const handleKeyWhenClosed = (event) => {
        const { key } = event;
        if (["ArrowDown", "ArrowUp", "Enter", " "].includes(key)) {
            openListbox(true);
        }
        else {
            handleKeyGeneral(event);
        }
    };
    const handleKeyWhenOpen = (event) => {
        const { altKey, key } = event;
        if (key === "ArrowDown") {
            activateOption(Math.min(activeIndex + 1, options.length - 1));
            consumeEvent(event);
        }
        else if ((altKey && key === "ArrowUp") || [" ", "Enter"].includes(key)) {
            selectActiveOption();
            consumeEvent(event);
        }
        else if (key === "ArrowUp") {
            activateOption(Math.max(activeIndex - 1, 0));
            consumeEvent(event);
        }
        else if (key === "PageDown") {
            activateOption(Math.min(activeIndex + 10, options.length - 1));
            consumeEvent(event);
        }
        else if (key === "PageUp") {
            activateOption(Math.max(activeIndex - 10, 0));
            consumeEvent(event);
        }
        else if (key === "Escape") {
            closeListbox();
            consumeEvent(event);
        }
        else {
            handleKeyGeneral(event);
        }
    };
    const handleKey = (event) => {
        if (open)
            return handleKeyWhenOpen(event);
        if (!open)
            return handleKeyWhenClosed(event);
    };
    const handleOptionClick = (optionIndex, event) => {
        selectOption(optionIndex);
        // Keeps the focus on the combobox
        event.preventDefault();
    };
    const handleAutoFill = (e) => {
        const autoFillValue = e.target.value;
        if (e.target.name === "state") {
            const mappedUsState = grins_utils_1.UsStateJsMap.get(e.target.value.toLowerCase()); // for Chrome
            return onSelectOption(options.findIndex(e => e.uiLabel === mappedUsState?.toString() ||
                e.uiLabel === autoFillValue));
        }
        onSelectOption(options.findIndex(e => e.uiLabel === autoFillValue));
    };
    const selectedOptionValue = selectedOption !== null ? options[selectedOption].uiLabel : "";
    return ((0, jsx_runtime_1.jsxs)("div", { "data-testid": `${testId}-container`, className: (0, classnames_1.default)("relative", className), children: [inputName && autoCompleteName ? ((0, jsx_runtime_1.jsx)("input", { role: "autofill-input", tabIndex: -1, "aria-hidden": "true", type: "text", className: "absolute opacity-0 pointer-events-none", name: inputName, autoComplete: autoCompleteName, onChange: handleAutoFill, value: selectedOptionValue })) : null, (0, jsx_runtime_1.jsxs)("div", { className: (0, classnames_1.default)((0, text_input_1.labelStyles)(), (0, text_input_1.disabledEl)(disabled)), onMouseDown: handleLabelMouseDown, children: [(0, jsx_runtime_1.jsx)("div", { id: idLabel, className: (0, classnames_1.default)((0, text_input_1.labelTextStyles)(isOffset, disabled)), children: label }), (0, jsx_runtime_1.jsx)("div", { className: `absolute z-10 mt-3.5 right-4  ${disabled ? "text-gr-faded opacity-80" : ""}`, "aria-hidden": true, children: open ? constants_1.upTriangle : constants_1.downTriangle }), (0, jsx_runtime_1.jsx)("div", { "data-testid": testId, ref: comboboxRef, className: (0, classnames_1.default)((0, text_input_1.inputStyles)(), (0, text_input_1.inputColors)(focused, "normal", disabled)), role: "combobox", "aria-labelledby": idLabel, "aria-activedescendant": open ? idOption(activeIndex) : "", "aria-controls": idListbox, "aria-expanded": open, "aria-haspopup": "listbox", onFocus: handleFocus, onBlur: handleBlur, onKeyDown: handleKey, tabIndex: 0, children: selectedOptionValue ? (selectedOptionValue) : ((0, jsx_runtime_1.jsx)("span", { "aria-hidden": true, children: constants_1.zeroWidthSpace })) })] }), helpText && (0, jsx_runtime_1.jsx)("div", { className: (0, classnames_1.default)((0, text_input_1.helpTextStyles)(false)), children: helpText }), (0, jsx_runtime_1.jsx)("ul", { "data-testid": `${testId}-listbox`, ref: listboxRef, id: idListbox, className: (0, classnames_1.default)("absolute left-0 right-0 mt-2 max-h-80 overflow-y-scroll", "bg-white border rounded border-gr-input-border-focus z-20", {
                    block: open,
                    hidden: !open,
                }), role: "listbox", children: options.map((option, index) => {
                    const isSelected = selectedOption === index;
                    const isActive = activeIndex === index;
                    return ((0, jsx_runtime_1.jsxs)("li", { "data-testid": `${option.uiLabel}-li`, className: (0, classnames_1.default)("p-3.5 border-2 rounded hover:bg-gr-bluestone", {
                            "border-white ": !isActive,
                            "border-gr-sapphire": isActive,
                        }), id: idOption(index), role: "option", "aria-selected": isSelected, 
                        // mouse down so we can prevent blur
                        onMouseDown: e => handleOptionClick(index, e), children: [option.uiLabel, isSelected && ((0, jsx_runtime_1.jsx)("span", { className: "font-bold ml-2", children: constants_1.checkmark }))] }, `${option.uiLabel}-${index}`));
                }) })] }));
}
exports.SelectComplexOptions = SelectComplexOptions;
